import { boolean, InferType, number, object, string, array } from 'yup';

export const BannerSchema = object({
  title: string().required(),
  description: string().nullable(),
  link: string().url().nullable(),
  image_web: string().url().required(),
  image_mobile: string().url().required(),
  is_active: number().nullable(),
});

export type IBanner = InferType<typeof BannerSchema>;

export const ProductSchema = object({
  id: number().required(),
  name: string().required(),
  sku: string(),
  price: string().nullable(),
  category_gender: string().nullable(),
  category_name: string().nullable(),
  main_photo: string().nullable(),
  description: string().nullable(),
  country_id: number().nullable(),
  category_id: number().nullable(),
  box_count: number().nullable(),
  dosage: string().nullable(),
  image: string().nullable(),
  length: string().nullable(),
  photos: array(
    object({
      id: number().required(),
      image: string().nullable(),
      mobile_image: string().nullable(),
      is_main: boolean().nullable(),
    })
  ).nullable(),
  width: string().nullable(),
  height: string().nullable(),
  recipe: number().nullable(),
  temperature: number().nullable(),
  is_active: boolean().nullable(),
  is_adult: number().nullable(),
  short_description: string().nullable(),
});

export type IProduct = InferType<typeof ProductSchema>;

export const CategorySchema = object({
  id: number().required(),
  name: string().required(),
  mobile_image: string().url().nullable(),
  image: string().url().nullable(),
  icon: string().url().nullable(),
  is_adult: number().nullable(),
  is_mainpage: boolean().nullable(),
  is_active: boolean().nullable(),
});

export type ICategory = InferType<typeof CategorySchema>;

export const CartProductSchema = object({
  id: number().required(),
  added_at: string().nullable(),
  basket_id: number().nullable(),
  pharmacy_stock_id: number().nullable(),
  product_id: number().nullable(),
  product: ProductSchema,
  quantity: number().required(),
});

export type ICartProduct = InferType<typeof CartProductSchema>;

export const CartSchema = object({
  id: number().required(),
  basket_items: array(CartProductSchema).required(),
});

export type ICart = InferType<typeof CartSchema>;

export const UserSchema = object({
  address: string().nullable(),
  first_name: string().nullable(),
  last_name: string().nullable(),
  phone: string().required(),
});

export type IUser = InferType<typeof UserSchema>;

export const LocationSchema = object({
  id: number().required(),
  name: string().required(),
  is_active: boolean(),
  region: string().nullable(),
  latitude: string().nullable(),
  longitude: string().nullable(),
});

export type ILocation = InferType<typeof LocationSchema>;

export interface IPagination<T> {
  meta: {
    current_page: number;
    last_page: number;
  };
  data: T[];
}

export const StoreSchema = object({
  address: string(),
  city: string(),
  city_id: number().nullable(),
  commission: number().nullable(),
  delivery_available: number().default(0),
  description: string().nullable(),
  email: string().nullable(),
  id: number().required(),
  is_active: number().nullable(),
  is_visible: number().nullable(),
  latitude: string().nullable(),
  logo: string().nullable(),
  longitude: string().nullable(),
  pickup_available: number().default(0),
  postal_code: string().nullable(),
  zid: string().nullable(),
  working_from: string().nullable(),
  working_to: string().nullable(),
});

export type IStore = InferType<typeof StoreSchema>;

export type IValueProduct = IProduct & { value: number };

export const FavoriteSchema = object({
  id: number().required(),
  product: ProductSchema,
});

export type IFavoriteProduct = InferType<typeof FavoriteSchema>;

export type TOrderStatus =
  | 'idle'
  | 'delivery'
  | 'ready'
  | 'accepted'
  | 'cancelled';

export const OrderProduct = object({
  id: number().required(),
  quantity: number(),
  product: object({
    id: number().required(),
    name: string().required(),
    main_photo: string().url().required(),
    price: number().required(),
    total_price: number().required(),
    category_name: string(),
  }),
});

export const OrderSchema = object({
  id: number().required(),
  user_id: number(),
  city_id: number(),
  delivery_address: string().nullable(),
  collection_code: string().nullable(),
  total_price: string(),
  status: number().required(),
  paid_at: string().nullable(),
  shipped_at: string().nullable(),
  comment: string().nullable(),
  payment_id: number().nullable(),
  delivery_type: string().required(),
  product_count: number().required(),
  items: array(OrderProduct).required(),
});

export type IOrder = InferType<typeof OrderSchema>;

export const CheckoutSchema = object({
  payment_url: string().url(),
  success: boolean(),
  order: object({
    city_id: number(),
    delivery_type: string().required(),
    delivery_address: string().nullable(),
    user_id: number().nullable(),
    status: number().required(),
    updated_at: string(),
    created_at: string(),
    id: number().required(),
    total_price: number().required(),
  }),
});

export type ICheckout = InferType<typeof CheckoutSchema>;
